/*
 * demo-fleet — DEV-ONLY synthetic fleet for exercising the map (density, clustering,
 * spread, and later the connection arcs) BEFORE the real fleet is large. Opt-in via
 * `?demo` (or `?demo=<n>`) on the URL — absent → nothing injected, real data only.
 *
 * One self-contained module, trivially removable. NOT a prod path: it's a visual dev
 * aid, gated on a query param, generated deterministically so it's stable across
 * re-renders (no flicker, no re-cluster).
 */
import type { CloudServer, FleetGateway, GatewayIds } from './fleet-client';

// A small inline city set for synthetic positions — kept LOCAL (not the shared 10k
// CITIES) so the big city dataset stays in the lazy map chunk, out of the app-shell
// boot. Weighted toward Vitesse's real footprint (US + EU) + a global spread.
interface DemoCity {
  name: string;
  cc: string;
  lat: number;
  lng: number;
}
const DEMO_CITIES: DemoCity[] = [
  { name: 'San Francisco', cc: 'US', lat: 37.77, lng: -122.42 },
  { name: 'Morgan Hill', cc: 'US', lat: 37.13, lng: -121.65 },
  { name: 'Los Angeles', cc: 'US', lat: 34.05, lng: -118.24 },
  { name: 'Austin', cc: 'US', lat: 30.27, lng: -97.74 },
  { name: 'Chicago', cc: 'US', lat: 41.88, lng: -87.63 },
  { name: 'New York', cc: 'US', lat: 40.71, lng: -74.01 },
  { name: 'Toronto', cc: 'CA', lat: 43.65, lng: -79.38 },
  { name: 'Mexico City', cc: 'MX', lat: 19.43, lng: -99.13 },
  { name: 'São Paulo', cc: 'BR', lat: -23.55, lng: -46.63 },
  { name: 'Buenos Aires', cc: 'AR', lat: -34.6, lng: -58.38 },
  { name: 'London', cc: 'GB', lat: 51.51, lng: -0.13 },
  { name: 'Paris', cc: 'FR', lat: 48.85, lng: 2.35 },
  { name: 'Lyon', cc: 'FR', lat: 45.76, lng: 4.84 },
  { name: 'Marseille', cc: 'FR', lat: 43.3, lng: 5.37 },
  { name: 'Strasbourg', cc: 'FR', lat: 48.57, lng: 7.75 },
  { name: 'Madrid', cc: 'ES', lat: 40.42, lng: -3.7 },
  { name: 'Barcelona', cc: 'ES', lat: 41.39, lng: 2.17 },
  { name: 'Berlin', cc: 'DE', lat: 52.52, lng: 13.4 },
  { name: 'Frankfurt', cc: 'DE', lat: 50.11, lng: 8.68 },
  { name: 'Munich', cc: 'DE', lat: 48.14, lng: 11.58 },
  { name: 'Amsterdam', cc: 'NL', lat: 52.37, lng: 4.9 },
  { name: 'Milan', cc: 'IT', lat: 45.46, lng: 9.19 },
  { name: 'Zurich', cc: 'CH', lat: 47.37, lng: 8.54 },
  { name: 'Stockholm', cc: 'SE', lat: 59.33, lng: 18.07 },
  { name: 'Warsaw', cc: 'PL', lat: 52.23, lng: 21.01 },
  { name: 'Dubai', cc: 'AE', lat: 25.2, lng: 55.27 },
  { name: 'Mumbai', cc: 'IN', lat: 19.08, lng: 72.88 },
  { name: 'Bangalore', cc: 'IN', lat: 12.97, lng: 77.59 },
  { name: 'Singapore', cc: 'SG', lat: 1.35, lng: 103.82 },
  { name: 'Tokyo', cc: 'JP', lat: 35.68, lng: 139.69 },
  { name: 'Seoul', cc: 'KR', lat: 37.57, lng: 126.98 },
  { name: 'Shanghai', cc: 'CN', lat: 31.23, lng: 121.47 },
  { name: 'Sydney', cc: 'AU', lat: -33.87, lng: 151.21 },
  { name: 'Johannesburg', cc: 'ZA', lat: -26.2, lng: 28.05 },
  { name: 'Cairo', cc: 'EG', lat: 30.04, lng: 31.24 },
  { name: 'Istanbul', cc: 'TR', lat: 41.01, lng: 28.98 },
];

const ORGS: Array<{ id: string; name: string }> = [
  { id: 'org_demo_setforge', name: 'Setforge' },
  { id: 'org_demo_mecatherm', name: 'Mecatherm' },
  { id: 'org_demo_rexel', name: 'Rexel' },
  { id: 'org_demo_veolia', name: 'Veolia' },
  { id: 'org_demo_sanofi', name: 'Sanofi' },
  { id: 'org_demo_kaliber', name: 'Kaliber' },
  { id: 'org_demo_alten', name: 'Alten' },
  { id: '', name: '' }, // unclaimed
];

const VERSIONS = ['0.1.9', '0.1.10', '0.1.11'];

/** Deterministic PRNG (mulberry32) — same input → same fleet, so the synthetic data
 *  is stable across re-renders (no flicker / re-cluster). */
function rng(seed: number): () => number {
  return () => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Is the synthetic fleet requested? (`?demo` or `?demo=<n>` on the URL.) */
export function isDemoMode(): boolean {
  return new URLSearchParams(location.search).has('demo');
}

/** How many synthetic gateways — `?demo=<n>` (capped), else 120. */
export function demoCount(): number {
  const v = Number(new URLSearchParams(location.search).get('demo'));
  return Number.isFinite(v) && v > 0 ? Math.min(Math.floor(v), 5000) : 120;
}

/** A few synthetic private cloud servers (our Hetzner boxes) — distinct nodes on the
 *  map. Stats present (hasStats) so the demo shows the super-admin view. */
function demoCloudServers(): CloudServer[] {
  return [
    {
      id: 'cloud_nbg1', name: 'Nuremberg', geo: 'Nuremberg, DE', lat: 49.45, lng: 11.08, hasStats: true,
      cpuPct: 31, memUsedGb: 22, memTotalGb: 64, diskUsedGb: 1840, diskTotalGb: 7680,
      uptimeS: 32 * 86400 + 14 * 3600, load1: 1.4, load5: 1.1, load15: 0.9,
      cpuModel: 'AMD EPYC 9454P', cpuCores: 48, os: 'Ubuntu 24.04.1 LTS', kernel: '6.8.0-41-generic', arch: 'x86_64', processCount: 412,
      activeConnections: 124, dbEntries: 13_900_000, powerW: 112.4,
      egressPct: 0.6, apiSuccessPct: 99.8, errorRatePct: 0.2, avgLatencyMs: 14, ingressPct: 0.9, serverType: 'ccx33', brokerMsgsPerSec: 1820,
    },
    {
      id: 'cloud_ash', name: 'Ashburn', geo: 'Ashburn, US', lat: 39.04, lng: -77.49, hasStats: true,
      cpuPct: 18, memUsedGb: 14, memTotalGb: 32, diskUsedGb: 410, diskTotalGb: 3840,
      uptimeS: 9 * 86400 + 3 * 3600, load1: 0.6, load5: 0.5, load15: 0.4,
      cpuModel: 'AMD EPYC 7502P', cpuCores: 32, os: 'Ubuntu 24.04.1 LTS', kernel: '6.8.0-41-generic', arch: 'x86_64', processCount: 248,
      activeConnections: 41, dbEntries: 4_200_000, powerW: 78.1,
      egressPct: 0.3, apiSuccessPct: 99.9, errorRatePct: 0.1, avgLatencyMs: 9, ingressPct: 0.5, serverType: 'cx32', brokerMsgsPerSec: 520,
    },
    {
      id: 'cloud_sin', name: 'Singapore', geo: 'Singapore, SG', lat: 1.35, lng: 103.82, hasStats: true,
      cpuPct: 9, memUsedGb: 6, memTotalGb: 16, diskUsedGb: 88, diskTotalGb: 960,
      uptimeS: 61 * 86400, load1: 0.2, load5: 0.3, load15: 0.2,
      cpuModel: 'Intel Xeon E-2388G', cpuCores: 16, os: 'Ubuntu 24.04.1 LTS', kernel: '6.8.0-41-generic', arch: 'x86_64', processCount: 173,
      activeConnections: 12, dbEntries: 980_000, powerW: 44.7,
      egressPct: 1.1, apiSuccessPct: 99.5, errorRatePct: 0.5, avgLatencyMs: 22, ingressPct: 1.4, serverType: 'cpx41', brokerMsgsPerSec: 140,
    },
  ];
}

/** Synthetic edge-IDS surface for the demo Security tab — exercises every section
 *  (severity split, top signatures/sources, OT-protocol breakdown, active blocks). */
function demoIds(r: () => number, status: FleetGateway['status']): GatewayIds {
  const alive = status === 'online' && r() < 0.92;
  const modeRoll = r();
  const mode = modeRoll < 0.6 ? 'monitor' : modeRoll < 0.85 ? 'protect' : 'off';
  const crit = Math.floor(r() * 4);
  const high = Math.floor(r() * 25);
  const med = Math.floor(r() * 120);
  const ip = () => `185.${Math.floor(r() * 255)}.${Math.floor(r() * 255)}.${Math.floor(r() * 255)}`;
  return {
    mode,
    status: alive ? 'running' : 'degraded',
    suricataAlive: alive,
    suricataVersion: '7.0.7',
    engineUptimeS: Math.floor(r() * 20 * 86400),
    packetsTotal: Math.floor(r() * 5e8),
    bytesTotal: Math.floor(r() * 4e11),
    kernelDrops: Math.floor(r() * 1e5),
    idsCpuPct: Math.round((3 + r() * 18) * 10) / 10,
    idsMemMb: Math.floor(150 + r() * 250),
    inspectedPerSec: Math.floor(500 + r() * 7500),
    invalidPackets: Math.floor(r() * 5000),
    flowsActive: Math.floor(200 + r() * 3000),
    flowsTotal: Math.floor(r() * 5e6),
    flowMemuseBytes: Math.floor(64e6 + r() * 256e6),
    tcpSessions: Math.floor(r() * 2e6),
    alertsTotal: crit + high + med,
    alertsInInterval: Math.floor(r() * 3),
    alertsCritical: crit,
    alertsHigh: high,
    alertsMedium: med,
    topSignatures: [
      { sid: 9100001, name: 'VITESSE-SCAN TCP SYN port scan (>30 SYN/30s per source)', count: Math.floor(5 + r() * 40), severity: 4 },
      { sid: 2013028, name: 'ET POLICY curl User-Agent Outbound', count: Math.floor(r() * 30), severity: 3 },
      { sid: 2100498, name: 'GPL ATTACK_RESPONSE id check returned root', count: Math.floor(r() * 6), severity: 5 },
    ].filter((s) => s.count > 0),
    topCategories: [
      { category: 'Attempted Information Leak', count: Math.floor(10 + r() * 40) },
      { category: 'Potentially Bad Traffic', count: Math.floor(r() * 30) },
    ],
    topSources: [
      { ip: ip(), count: Math.floor(5 + r() * 40) },
      { ip: `10.1.10.${100 + Math.floor(r() * 150)}`, count: Math.floor(r() * 20) },
    ].filter((s) => s.count > 0),
    appLayerFlows: [
      { proto: 'tls', count: Math.floor(r() * 5000) },
      { proto: 'http', count: Math.floor(r() * 3000) },
      { proto: 'dns', count: Math.floor(r() * 2000) },
      { proto: 'modbus', count: Math.floor(r() * 800) },
      { proto: 's7comm', count: Math.floor(r() * 400) },
      { proto: 'enip', count: Math.floor(r() * 200) },
    ].filter((p) => p.count > 0),
    blocksTotal: mode === 'protect' ? Math.floor(r() * 8) : 0,
    activeBlocks:
      mode === 'protect' && r() < 0.5
        ? [{ srcIp: ip(), iface: 1, reason: 1, signatureId: 9100001 }]
        : [],
  };
}

/** Generate `n` synthetic gateways spread across the world + their org-name map +
 *  a handful of cloud servers. */
export function demoFleet(n = demoCount()): {
  gateways: FleetGateway[];
  orgNames: Map<string, string>;
  cloudServers: CloudServer[];
} {
  const now = Date.now();
  const gateways: FleetGateway[] = [];
  for (let i = 0; i < n; i++) {
    const r = rng((i + 1) * 2654435761);
    const city = DEMO_CITIES[Math.floor(r() * DEMO_CITIES.length)];
    const org = ORGS[Math.floor(r() * ORGS.length)];
    const roll = r();
    const status: FleetGateway['status'] = roll < 0.72 ? 'online' : roll < 0.9 ? 'offline' : 'unknown';
    const named = r() < 0.6;
    gateways.push({
      deviceFingerprint: `demo${i.toString(36).padStart(4, '0')}${Math.floor(r() * 1e9).toString(36)}`,
      orgId: org.id,
      displayName: named ? `${org.name || 'Edge'} ${city.name} ${1 + Math.floor(r() * 9)}` : '',
      status,
      softwareVersion: VERSIONS[Math.floor(r() * VERSIONS.length)],
      hostname: `gw-${city.name.toLowerCase().replace(/[^a-z]/g, '')}-${i}`,
      geo: `${city.name}, ${city.cc}`,
      lat: city.lat + (r() - 0.5) * 0.8, // spread within the metro so same-city boxes
      lng: city.lng + (r() - 0.5) * 0.8, // separate when you click-to-zoom into a cluster
      nodeKind: r() < 0.85 ? 'edge' : 'cloud',
      revoked: r() < 0.04,
      firstSeenMs: now - Math.floor(r() * 200) * 86400000,
      lastSeenMs:
        status === 'online' ? now - Math.floor(r() * 60000) : status === 'offline' ? now - 3 * 3600000 : now - 10 * 60000,
      localIp: `10.${Math.floor(r() * 255)}.${Math.floor(r() * 255)}.${1 + Math.floor(r() * 250)}`,
      publicIp: `${1 + Math.floor(r() * 223)}.${Math.floor(r() * 255)}.${Math.floor(r() * 255)}.${1 + Math.floor(r() * 250)}`,
      otIp: '',
      wifiIp: '',
      itMac: '',
      otMac: '',
      wifiMac: '',
      tier: r() < 0.3 ? 'pro' : '',
      healthTsMs: Date.now(),
      cpuPct: Math.round(8 + r() * 70),
      memPct: Math.round(15 + r() * 60),
      memTotalGb: 16,
      memUsedGb: Math.round((3 + r() * 8) * 10) / 10,
      tempC: Math.round(38 + r() * 22),
      uptimeS: Math.floor(r() * 90 * 86400),
      diskUsedGb: Math.round((8 + r() * 18) * 10) / 10,
      diskTotalGb: r() < 0.5 ? 32 : 64,
      processCount: Math.round(140 + r() * 120),
      load1: Math.round(r() * 3.5 * 100) / 100,
      cpuCores: r() < 0.6 ? 4 : 8,
      powerW: Math.round((4 + r() * 6) * 10) / 10,
      packetDropPct: r() < 0.7 ? 0 : Math.round(r() * 3 * 10) / 10,
      os: 'Ubuntu 24.04.1 LTS',
      brokerMsgsPerSec: status === 'online' ? Math.round(40 + r() * 500) : 0,
      cpuModel: 'Intel N100',
      cloudLatencyMs: status === 'online' ? Math.round(8 + r() * 60) : 0,
      ids: r() < 0.65 ? demoIds(r, status) : undefined, // ~65% run the IDS
    });
  }
  // A deliberately CO-LOCATED cluster — all behind ONE public IP → identical resolved
  // geo, like the real office (a laptop + the MU + the CM5 + bench boxes sharing a WAN
  // address). Exercises the map's co-location fan (fannedPositions): without it these
  // stack into a single chip and hide each other; with it all N fan out side-by-side.
  const officeIp = '76.21.8.44';
  const officeBoxes = ["Max's Macbook", 'MU — mu-dev', 'CM5 controller', 'Bench S7-1500', 'Lab spare'];
  officeBoxes.forEach((name, i) => {
    const r = rng(990000 + i);
    gateways.push({
      deviceFingerprint: `demoofc${i}${Math.floor(r() * 1e9).toString(36)}`,
      orgId: '',
      displayName: name,
      status: 'online',
      softwareVersion: VERSIONS[i % VERSIONS.length],
      hostname: name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      geo: 'Morgan Hill, US',
      lat: 37.13, // identical point for every office box → triggers the fan
      lng: -121.65,
      nodeKind: 'edge',
      revoked: false,
      firstSeenMs: now - 30 * 86400000,
      lastSeenMs: now - Math.floor(r() * 30000),
      localIp: `10.1.10.${100 + i}`,
      publicIp: officeIp,
      otIp: '',
      wifiIp: '',
      itMac: '',
      otMac: '',
      wifiMac: '',
      tier: '',
      healthTsMs: Date.now(),
      cpuPct: Math.round(8 + r() * 50),
      memPct: Math.round(20 + r() * 50),
      memTotalGb: 8,
      memUsedGb: Math.round((1.5 + r() * 4) * 10) / 10,
      tempC: Math.round(40 + r() * 18),
      uptimeS: Math.floor(r() * 30 * 86400),
      diskUsedGb: Math.round((8 + r() * 18) * 10) / 10,
      diskTotalGb: 64,
      processCount: Math.round(150 + r() * 80),
      load1: Math.round(r() * 3 * 100) / 100,
      cpuCores: 8,
      powerW: Math.round((5 + r() * 5) * 10) / 10,
      packetDropPct: 0,
      os: 'Ubuntu 24.04.1 LTS',
      brokerMsgsPerSec: Math.round(60 + r() * 700),
      cpuModel: 'Intel N100',
      cloudLatencyMs: Math.round(6 + r() * 30),
      ids: i < 3 ? demoIds(r, 'online') : undefined,
    });
  });
  const orgNames = new Map<string, string>();
  for (const o of ORGS) if (o.id) orgNames.set(o.id, o.name);
  return { gateways, orgNames, cloudServers: demoCloudServers() };
}
